import React, { memo, useCallback, useMemo } from 'react';
import { TextField, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useSelector } from 'react-redux';

import { activeUserHasPermission } from 'services/user/redux';
import { PermissionType } from 'services/permissions';
import { HierarchicalDimensionOption } from './types';
import type { HierarchicalDimensionValue } from './types';

export interface DimensionsAutocompleteProps {
  dimensions: HierarchicalDimensionOption[];
  onDimensionChange: (dimensions: HierarchicalDimensionOption[]) => void;
  permissions?: PermissionType[];
  disabled?: boolean;
}

/**
 * Extract all level selections from a selected value by traversing up the tree
 * Uses a map for O(1) lookup instead of O(n) find()
 */
const getSelectionPath = (
  selectedValue: HierarchicalDimensionValue,
  hierarchy: HierarchicalDimensionValue[]
): { [level: number]: string } => {
  const path: { [level: number]: string } = {};

  // Build a map for O(1) parent lookup
  const valueMap = new Map<string, HierarchicalDimensionValue>();
  hierarchy.forEach((v) => valueMap.set(v.id, v));

  let current: HierarchicalDimensionValue | undefined = selectedValue;

  while (current) {
    path[current.level] = current.id;
    current = current.parentId ? valueMap.get(current.parentId) : undefined;
  }

  return path;
};

/**
 * Build fully qualified name in Intuit format
 * e.g., "ParentClass:SubClassA" or "Marketing:Online"
 * Uses a map for O(1) lookup instead of O(n) find()
 */
const buildFullyQualifiedName = (
  value: HierarchicalDimensionValue,
  hierarchy: HierarchicalDimensionValue[],
  separator: string = ':'
): string => {
  const path: string[] = [];

  // Build a map for O(1) parent lookup
  const valueMap = new Map<string, HierarchicalDimensionValue>();
  hierarchy.forEach((v) => valueMap.set(v.id, v));

  let current: HierarchicalDimensionValue | undefined = value;

  while (current) {
    path.unshift(current.name);
    current = current.parentId ? valueMap.get(current.parentId) : undefined;
  }

  return path.join(separator);
};

/**
 * Dimensions Autocomplete - QuickBooks Online Style
 */
const DimensionsAutocomplete: React.FC<DimensionsAutocompleteProps> = ({
  dimensions,
  onDimensionChange,
  permissions = [],
  disabled = false,
}) => {
  const canEdit = useSelector(activeUserHasPermission(permissions));
  const isDisabled = disabled || !canEdit;

  const flattenDimension = useCallback(
    (dimension: HierarchicalDimensionOption) => {
      return dimension.hierarchy.map((value) => {
        const fullyQualifiedName =
          value.fullyQualifiedLabel ||
          buildFullyQualifiedName(value, dimension.hierarchy, ':');

        return {
          ...value,
          fullyQualifiedName,
        };
      });
    },
    []
  );

  const getSelectedValue = useCallback(
    (
      dimension: HierarchicalDimensionOption
    ): HierarchicalDimensionValue | null => {
      let selectedValueId: string | null = null;
      for (let level = dimension.maxLevel; level >= 0; level--) {
        if (dimension.selectedValues[level]) {
          selectedValueId = dimension.selectedValues[level];
          break;
        }
      }
      return selectedValueId
        ? dimension.hierarchy.find((v) => v.id === selectedValueId) || null
        : null;
    },
    []
  );

  const handleDimensionChange = useCallback(
    (dimensionId: string) =>
      (_event: any, selectedValue: HierarchicalDimensionValue | null) => {
        const updatedDimensions = dimensions.map((dim) => {
          if (dim.id !== dimensionId) return dim;

          if (!selectedValue) {
            const clearedValues: { [level: number]: string | null } = {};
            for (let i = 0; i <= dim.maxLevel; i++) clearedValues[i] = null;
            return { ...dim, selectedValues: clearedValues };
          }

          const selectionPath = getSelectionPath(selectedValue, dim.hierarchy);
          const newSelectedValues: { [level: number]: string | null } = {};
          for (let i = 0; i <= dim.maxLevel; i++) {
            newSelectedValues[i] = selectionPath[i] || null;
          }

          return {
            ...dim,
            selectedValues: newSelectedValues,
          };
        });

        onDimensionChange(updatedDimensions);
      },
    [dimensions, onDimensionChange]
  );

  if (!dimensions || dimensions.length === 0) return null;

  return (
    <>
      {dimensions.map((dimension) => {
        const flattenedOptions = useMemo(
          () => flattenDimension(dimension),
          [dimension, flattenDimension]
        );
        const selectedValue = getSelectedValue(dimension);
        const selectedOption = selectedValue
          ? flattenedOptions.find((opt) => opt.id === selectedValue.id)
          : null;

        return (
          <div key={dimension.id} style={{ marginBottom: '-20px' }}>
            <Autocomplete
              fullWidth
              className="redesign"
              options={flattenedOptions}
              disabled={isDisabled}
              value={selectedOption}
              onChange={handleDimensionChange(dimension.id)}
              isOptionEqualToValue={(option, val) => option?.id === val?.id}
              getOptionLabel={(option) =>
                option.fullyQualifiedName || option.name
              }
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Typography variant="body2">
                    {option.fullyQualifiedName || option.name}
                  </Typography>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  className="redesign"
                  variant="outlined"
                  size="small"
                  label={dimension.name}
                  placeholder={`Search or select ${dimension.name}...`}
                  data-qa={`dimension-${dimension.id}`}
                  required={dimension.required}
                  error={dimension.required && !selectedValue}
                />
              )}
            />
          </div>
        );
      })}
    </>
  );
};

export default memo(DimensionsAutocomplete);
